import "bootstrap/dist/css/bootstrap.min.css";
import { get, onValue, push, query, orderByChild, equalTo, ref, set, update } from "firebase/database";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../App.css";
import MaskedInput from "../components/MaskedInput";
import { validatePhoneMessage } from "../utils/fieldValidation";
import { db } from "../Firebase";

/* ================= Reusable Field ================= */
const Field = ({ label, children, fullWidth = false }) => (
  <div className={`mb-3 ${fullWidth ? "col-12" : "col-12 col-md-6 col-lg-4"}`}>
    <label
      className="form-label fw-semibold"
      style={{
        color: "#2C3E50",
        fontWeight: "600",
        fontSize: "14px",
        marginBottom: "8px",
        textShadow: "1px 1px 2px rgba(0,0,0,0.1)",
      }}
    >
      {label}
    </label>
    {children}
  </div>
);

/* ================= Unique 6 Digit ID ================= */
const generateUniqueId = () =>
  Math.floor(100000 + Math.random() * 900000).toString();

const RegistrationForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const [isEditMode, setIsEditMode] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  /* ================= MASTER DATA ================= */
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [panchayats, setPanchayats] = useState([]);
  const [grams, setGrams] = useState([]);
  const [urbanAreas, setUrbanAreas] = useState([]);
  const [mandirs, setMandirs] = useState([]);
  const [jatiPrakars, setJatiPrakars] = useState([]);
  const [upjatiPrakars, setUpjatiPrakars] = useState([]);
  const [gotras, setGotras] = useState([]);
  const [moors, setMoors] = useState([]);

  /* ================= FORM STATE ================= */
  const [form, setForm] = useState({
    country: "भारत",
    state: "",
    district: "",
    block: "",
    areaType: "",
    panchayat: "",
    gram: "",
    urbanArea: "",
    address: "",
    totalMembers: "",
    headName: "",
    popularName: "",
    mobile: "",
    motherTongue: "",

    mandir: "",
    jatiPrakar: "",
    upjatiPrakar: "",
    gotra: "",
    moor: "",

    familyStatus: "",
    houseStatus: "",

    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    const mandirRef = ref(db, "masters/jainMandir");

    onValue(mandirRef, (snapshot) => {
      if (snapshot.exists()) {
        const list = Object.entries(snapshot.val()).map(([id, v]) => ({
          id,
          ...v,
        })); // 🔑 Changed to include id and structure
        setMandirs(list);
      }
    });
  }, []);
  /* ================= FETCH MASTERS ================= */
  useEffect(() => {
    const fetchData = (path, setter) =>
      onValue(ref(db, path), (snap) =>
        setter(
          snap.exists()
            ? Object.entries(snap.val()).map(([id, v]) => ({ id, ...v }))
            : [],
        ),
      );

    fetchData("masters/states", setStates);
    fetchData("masters/cities", setCities);
    fetchData("masters/blocks", setBlocks);
    fetchData("masters/panchayats", setPanchayats);
    fetchData("masters/grams", setGrams);
    fetchData("masters/urbanAreas", setUrbanAreas);
    fetchData("masters/jatiPrakars", setJatiPrakars);
    fetchData("masters/upjatiPrakars", setUpjatiPrakars);
    fetchData("masters/gotras", setGotras);
    fetchData("masters/moors", setMoors);
  }, []);

  /* ================= SHOW TERMS MODAL ================= */
  useEffect(() => {
    if (!editId) {
      setShowTermsModal(true);
    }
  }, [editId]);

  /* ================= EDIT MODE ================= */
  useEffect(() => {
    if (editId) {
      setIsEditMode(true);
      const regRef = ref(db, `registration/${editId}`);
      onValue(regRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          if (data.basic) {
            setForm({
              country: data.basic.country || "भारत",
              state: data.basic.state || "",
              district: data.basic.city || "",
              block: data.basic.block || "",
              areaType: data.basic.panchayat ? "Rural" : "Urban",
              panchayat: data.basic.panchayat || "",
              gram: data.basic.gram || "",
              urbanArea: data.basic.urbanArea || "",
              address: data.basic.address || "",
              totalMembers: data.basic.Totalmember || "",
              headName: data.basic.headName || "",
              popularName: data.basic.displayName || "",
              mobile: data.basic.mobile || "",
              motherTongue: data.basic.language || "",
              mandir: data.basic.Mandirtype || "",
              jatiPrakar: data.basic.jatiPrakar || "",
              upjatiPrakar: data.basic.upjatiPrakar || "",
              gotra: data.basic.gotra || "",
              moor: data.basic.moor || "",
              familyStatus: data.basic.familyType || "",
              houseStatus: data.basic.houseType || "",
              password: data.meta?.password || "",
              confirmPassword: data.meta?.password || "",
            });
          }
        }
      });
    }
  }, [editId]);

  /* ================= HANDLE CHANGE ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      if (name === "jatiPrakar")
        return {
          ...prev,
          jatiPrakar: value,
          upjatiPrakar: "",
          gotra: "",
          moor: "",
        };
      if (name === "upjatiPrakar")
        return { ...prev, upjatiPrakar: value, gotra: "", moor: "" };
      if (name === "gotra") return { ...prev, gotra: value, moor: "" };
      if (name === "district") return { ...prev, district: value, mandir: "" }; // Reset mandir when district changes
      return { ...prev, [name]: value };
    });
  };

  /* ================= COMMON PAYLOAD ================= */
  const createPayload = () => ({
    country: form.country,
    state: form.state,
    city: form.district,
    block: form.block,
    areaType: form.areaType,
    panchayat: form.areaType === "Rural" ? form.panchayat : "",
    gram: form.areaType === "Rural" ? form.gram : "",
    urbanArea: form.areaType === "Urban" ? form.urbanArea : "",
    address: form.address,
    headName: form.headName,
    displayName: form.popularName,
    mobile: form.mobile,
    language: form.motherTongue,
    familyType: form.familyStatus,
    houseType: form.houseStatus,
    Totalmember: form.totalMembers,
    Mandirtype: form.mandir,
    jatiPrakar: form.jatiPrakar,
    upjatiPrakar: form.upjatiPrakar,
    gotra: form.gotra,
    moor: form.moor,
    createdAt: Date.now(),
  });

  /* ================= VALIDATION ================= */
  const validateForm = async () => {
    if (form.mobile) {
      const phoneErr = validatePhoneMessage(form.mobile);
      if (phoneErr) {
        alert(phoneErr);
        return false;
      }
    }
    if (form.confirmPassword && form.password !== form.confirmPassword) {
      alert("पासवर्ड मेल नहीं खा रहा");
      return false;
    }
    if (!isEditMode && form.mobile) {
      const snap = await get(query(ref(db, "registration"), orderByChild("basic/mobile"), equalTo(form.mobile)));
      if (snap.exists()) {
        alert("❌ यह मोबाइल नंबर पहले से पंजीकृत है।");
        return false;
      }
    }
    return true;
  };

  /* ================= SAVE ONLY ================= */
  const handleSaveOnly = async () => {
    if (!(await validateForm())) return;

    const payload = createPayload();

    if (isEditMode) {
      await update(ref(db, `registration/${editId}/basic`), payload);
      alert("✅ डेटा सफलतापूर्वक अपडेट हो गया है");
      navigate(`/information/${editId}`);
    } else {
      const regId = generateUniqueId();
      await set(ref(db, `registration/${regId}`), {
        meta: {
          contactNumber: form.mobile,
          password: form.password,
          createdAt: Date.now(),
        },
        basic: payload,
      });

      alert(
        `✅ डेटा सफलतापूर्वक सेव हो गया है\n\n` +
          `🆔 आपकी यूनिक ID: ${regId}\n\n` +
          `⚠️ कृपया इस ID को लिखकर सुरक्षित रखें।\n` +
          `यह ID भविष्य में अपना डेटा खोजने और एडिट करने के लिए बहुत ज़रूरी है।\n\n` +
          `🔐 आपका पासवर्ड: ${form.password}\n\n` +
          `⚠️ कृपया अपना पासवर्ड भी सुरक्षित रखें।`,
      );
      navigate(`/`);
    }
  };

  const [showMandirForm, setShowMandirForm] = useState(false);
  const [newMandirNickName, setNewMandirNickName] = useState("");
  const [newMandirFullName, setNewMandirFullName] = useState("");
  const [newMandirAddress, setNewMandirAddress] = useState("");
  const [newMandirRemark, setNewMandirRemark] = useState("");
  const [mandirSubmitting, setMandirSubmitting] = useState(false);

  const handleSubmitNewMandir = async () => {
    if (!newMandirNickName.trim()) return alert("मंदिर का नाम आवश्यक है");
    if (!form.state || !form.district) return alert("पहले राज्य और शहर चुनें");
    setMandirSubmitting(true);
    try {
      await push(ref(db, "masters/jainMandir"), {
        nickName: newMandirNickName.trim(),
        fullName: newMandirFullName.trim(),
        address: newMandirAddress.trim(),
        remark: newMandirRemark.trim(),
        state: form.state,
        district: form.district,
        pendingApproval: true,
        addedAt: Date.now(),
      });
      setForm((prev) => ({ ...prev, mandir: newMandirNickName.trim() }));
      setNewMandirNickName("");
      setNewMandirFullName("");
      setNewMandirAddress("");
      setNewMandirRemark("");
      setShowMandirForm(false);
      alert(
        "मंदिर सफलतापूर्वक जोड़ा गया ✅\nयह Admin की स्वीकृति के बाद सामान्य रूप से दिखेगा।",
      );
    } catch {
      alert("त्रुटि हुई ❌");
    } finally {
      setMandirSubmitting(false);
    }
  };
  const handleSaveNext = async (e) => {
    e.preventDefault();

    if (!(await validateForm())) return;

    const payload = createPayload();

    if (isEditMode) {
      await update(ref(db, `registration/${editId}/basic`), payload);
      alert("✅ डेटा सफलतापूर्वक अपडेट हो गया है");
      navigate(`/information/${editId}`);
    } else {
      const regId = generateUniqueId();

      await set(ref(db, `registration/${regId}`), {
        meta: {
          contactNumber: form.mobile,
          password: form.password,
          createdAt: Date.now(),
        },
        basic: payload,
      });

      alert(
        `✅ डेटा सफलतापूर्वक सेव हो गया है\n\n` +
          `🆔 आपकी यूनिक ID: ${regId}\n\n` +
          `⚠️ कृपया इस ID को लिखकर सुरक्षित रखें।\n` +
          `यह ID भविष्य में अपना डेटा खोजने और एडिट करने के लिए बहुत ज़रूरी है।\n\n` +
          `🔐 आपका पासवर्ड: ${form.password}\n\n` +
          `⚠️ कृपया अपना पासवर्ड भी सुरक्षित रखें।`,
      );

      navigate(`/information/${regId}`);
    }
  };

  /* ================= UI ================= */
  return (
    <>
      {/* Terms Modal */}
      {showTermsModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-success text-white">
                <h5 className="modal-title">📜 नियम व शर्तें</h5>
              </div>
              <div className="modal-body">
                <p
                  className="mb-0"
                  style={{
                    color: "#2C3E50",
                    fontWeight: "500",
                    lineHeight: "1.6",
                  }}
                >
                  इस फॉर्म में दी गई सभी जानकारी सत्य है। गलत जानकारी देने पर
                  रिकॉर्ड रद्द किया जा सकता है।
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => setShowTermsModal(false)}
                >
                  आगे बढ़ें
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container my-3 my-md-5 px-3 px-md-4">
        <div
          className="card shadow-lg border-0"
          style={{ borderRadius: "20px", overflow: "hidden" }}
        >
          <div
            className="card-header text-white fw-bold text-center py-3 py-md-4"
            style={{
              background: "linear-gradient(135deg, #0D9488 0%, #0B7A6F 100%)",
              fontSize: "1.1rem",
              textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
              letterSpacing: "0.5px",
            }}
          >
            {isEditMode
              ? "✏️ जैन परिवार पंजीकरण संपादित करें"
              : "📝 जैन परिवार पंजीकरण फॉर्म"}
          </div>

          <div className="card-body p-2 p-md-4" style={{ padding: "1.5rem" }}>
            <form onSubmit={handleSaveNext}>
              {/* ===== LOCATION ===== */}
              <h5
                className="text-primary mb-3 mb-md-4"
                style={{
                  color: "#0D9488",
                  fontWeight: "700",
                  fontSize: "1.1rem",
                  borderBottom: "3px solid #0D9488",
                  paddingBottom: "10px",
                  textShadow: "1px 1px 2px rgba(0,0,0,0.1)",
                }}
              >
                📍 स्थान की जानकारी
              </h5>
              <div className="row g-2 g-md-3">
                <Field label="देश">
                  <input
                    className="form-control"
                    value="भारत"
                    disabled
                    style={{
                      backgroundColor: "#F8F9FA",
                      color: "#2C3E50",
                      fontWeight: "600",
                      border: "2px solid #E9ECEF",
                    }}
                  />
                </Field>

                <Field label="राज्य *">
                  <select
                    className="form-select"
                    name="state"
                    value={form.state}
                    onChange={handleChange}
                    required
                  >
                    <option value="">राज्य चुनें *</option>
                    {states.map((s) => (
                      <option key={s.id}>{s.name}</option>
                    ))}
                  </select>
                </Field>

                <Field label="शहर *">
                  <select
                    className="form-select"
                    name="district"
                    value={form.district}
                    onChange={handleChange}
                    required
                  >
                    <option value="">शहर चुनें *</option>
                    {cities
                      .filter((c) => c.state === form.state)
                      .map((c) => (
                        <option key={c.id}>{c.name}</option>
                      ))}
                  </select>
                </Field>

                <Field label="ब्लॉक">
                  <select
                    className="form-select"
                    name="block"
                    value={form.block}
                    onChange={handleChange}
                  >
                    <option value="">चयन करें</option>
                    {blocks
                      .filter((b) => b.city === form.district)
                      .map((b) => (
                        <option key={b.id}>{b.name}</option>
                      ))}
                  </select>
                </Field>

                <Field label="शहरी / ग्रामीण">
                  <select
                    className="form-select"
                    name="areaType"
                    value={form.areaType}
                    onChange={handleChange}
                  >
                    <option value="">चयन करें</option>
                    <option value="Urban">शहरी</option>
                    <option value="Rural">ग्रामीण</option>
                  </select>
                </Field>

                {form.areaType === "Rural" && (
                  <>
                    <Field label="ग्राम पंचायत">
                      <select
                        className="form-select"
                        name="panchayat"
                        value={form.panchayat}
                        onChange={handleChange}
                      >
                        <option value="">ग्राम पंचायत चुनें</option>
                        {panchayats
                          .filter((p) => p.block === form.block)
                          .map((p) => (
                            <option key={p.id}>{p.name}</option>
                          ))}
                      </select>
                    </Field>

                    <Field label="ग्राम">
                      <select
                        className="form-select"
                        name="gram"
                        value={form.gram}
                        onChange={handleChange}
                      >
                        <option value="">ग्राम चुनें</option>
                        {grams
                          .filter(
                            (g) =>
                              g.block === form.block &&
                              g.panchayatName === form.panchayat,
                          )
                          .map((g) => (
                            <option key={g.id}>{g.name}</option>
                          ))}
                      </select>
                    </Field>
                  </>
                )}

                {form.areaType === "Urban" && (
                  <Field label="शहरी क्षेत्र">
                    <select
                      className="form-select"
                      name="urbanArea"
                      value={form.urbanArea}
                      onChange={handleChange}
                    >
                      <option value="">शहरी क्षेत्र चुनें</option>
                      {urbanAreas
                        .filter((u) => u.city === form.district)
                        .map((u) => (
                          <option key={u.id}>{u.name}</option>
                        ))}
                    </select>
                  </Field>
                )}

                <Field label="घर का पूरा पता" fullWidth={true}>
                  <textarea
                    className="form-control"
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    placeholder="घर का पूरा पता दर्ज करें"
                    rows="2"
                  />
                </Field>
              </div>

              <hr className="my-4" />

              {/* ===== FAMILY ===== */}
              <h5
                className="text-primary mb-3 mb-md-4"
                style={{
                  color: "#0D9488",
                  fontWeight: "700",
                  fontSize: "1.1rem",
                  borderBottom: "3px solid #0D9488",
                  paddingBottom: "10px",
                  textShadow: "1px 1px 2px rgba(0,0,0,0.1)",
                }}
              >
                👨‍👩‍👧‍👦 परिवार की जानकारी
              </h5>
              <div className="row g-2 g-md-3">
                <Field label="परिवार के मुखिया का नाम *">
                  <input
                    className="form-control"
                    name="headName"
                    value={form.headName}
                    onChange={handleChange}
                    placeholder="परिवार के मुखिया का नाम दर्ज करें"
                    required
                  />
                </Field>

                <Field label="समाज में प्रचलित नाम">
                  <input
                    className="form-control"
                    name="popularName"
                    value={form.popularName}
                    onChange={handleChange}
                    placeholder="समाज में प्रचलित नाम दर्ज करें"
                  />
                </Field>

                <Field label="मोबाइल *">
                  <MaskedInput
                    type="phone"
                    className="form-control"
                    name="mobile"
                    value={form.mobile}
                    onChange={handleChange}
                    placeholder="मोबाइल नंबर दर्ज करें"
                    maxLength="14"
                    required
                  />
                </Field>

                <Field label="परिवार में कुल सदस्य *">
                  <input
                    type="number"
                    className="form-control"
                    name="totalMembers"
                    value={form.totalMembers}
                    onChange={handleChange}
                    min="1"
                    placeholder="कुल सदस्यों की संख्या दर्ज करें"
                    required
                  />
                </Field>

                <Field label="संचार/ सम्पर्क भाषा">
                  <select
                    className="form-select"
                    name="motherTongue"
                    value={form.motherTongue}
                    onChange={handleChange}
                  >
                    <option value="">संचार/ सम्पर्क भाषा चुनें</option>
                    <option value="English">English</option>
                    <option value="हिंदी">हिंदी</option>
                    <option value="संस्कृत">संस्कृत</option>
                    <option value="गुजराती">गुजराती</option>
                    <option value="मारवाड़ी">मारवाड़ी</option>
                    <option value="राजस्थानी">राजस्थानी</option>
                    <option value="मराठी">मराठी</option>
                    <option value="पंजाबी">पंजाबी</option>
                    <option value="कन्नड़">कन्नड़</option>
                    <option value="तमिल">तमिल</option>
                    <option value="तेलुगु">तेलुगु</option>
                    <option value="उड़िया">उड़िया</option>
                    <option value="असमिया">असमिया</option>
                    <option value="बंगाली">बंगाली</option>
                    <option value="बोडो">बोडो</option>
                    <option value="डोगरी">डोगरी</option>
                    <option value="कश्मीरी">कश्मीरी</option>
                    <option value="कोंकणी">कोंकणी</option>
                    <option value="मैथिली">मैथिली</option>
                    <option value="मलयालम">मलयालम</option>
                    <option value="मणिपुरी">मणिपुरी</option>
                    <option value="नेपाली">नेपाली</option>
                    <option value="संताली">संताली</option>
                    <option value="सिंधी">सिंधी</option>
                    <option value="उर्दू">उर्दू</option>
                  </select>
                </Field>

                <Field label="मूलभाषा / मातृभाषा">
                  <select className="form-select">
                    <option value="">मूलभाषा / मातृभाषा चुनें</option>
                    <option value="प्राकृत">प्राकृत</option>
                  </select>
                </Field>

                <Field label="परिवार की स्थिति">
                  <select
                    className="form-select"
                    name="familyStatus"
                    value={form.familyStatus}
                    onChange={handleChange}
                  >
                    <option value="">परिवार की स्थिति चुनें</option>
                    <option value="संयुक्त परिवार">संयुक्त परिवार</option>
                    <option value="एकल परिवार">एकल परिवार</option>
                    <option value="विस्तारित परिवार">विस्तारित परिवार</option>
                  </select>
                </Field>

                <Field label="आवास की स्थिति">
                  <select
                    className="form-select"
                    name="houseStatus"
                    value={form.houseStatus}
                    onChange={handleChange}
                  >
                    <option value="">आवास की स्थिति चुनें</option>
                    <option value="स्वयं का मकान">स्वयं का मकान</option>
                    <option value="किराए का मकान">किराए का मकान</option>
                    <option value="सरकारी आवास">सरकारी आवास</option>
                    <option value="अन्य">अन्य</option>
                  </select>
                </Field>

                <Field label="जैन मंदिर चुनें *" fullWidth={true}>
                  <select
                    className="form-select"
                    name="mandir"
                    value={form.mandir}
                    onChange={handleChange}
                    required
                  >
                    <option value="">
                      जैन मंदिर चुनें - जिसके आप सदस्य है/असामी है/पूजन करने
                      जाते है
                    </option>
                    {mandirs
                      .filter((m) => m.district === form.district)
                      .map((m, i) => (
                        <option key={i} value={m.nickName}>
                          {m.nickName}
                          {m.pendingApproval === true ? " ⏳" : ""}
                        </option>
                      ))}
                  </select>
                  <div className="mt-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowMandirForm((v) => !v)}
                    >
                      🕌 मंदिर नहीं मिला? यहाँ जोड़ें
                    </button>
                  </div>
                  {showMandirForm && (
                    <div
                      className="mt-3 p-3 border rounded"
                      style={{ background: "#f0fdf4" }}
                    >
                      <h6 className="fw-bold text-success mb-3">
                        🕌 नया जैन मंदिर जोड़ें
                      </h6>
                      <div className="row g-2">
                        <div className="col-12 col-md-6">
                          <label className="form-label fw-semibold">
                            मंदिर का नाम (Nick Name) *
                          </label>
                          <input
                            className="form-control"
                            value={newMandirNickName}
                            onChange={(e) =>
                              setNewMandirNickName(e.target.value)
                            }
                            placeholder="जैसे: शांतिनाथ मंदिर"
                          />
                        </div>
                        <div className="col-12 col-md-6">
                          <label className="form-label fw-semibold">
                            पूरा नाम
                          </label>
                          <input
                            className="form-control"
                            value={newMandirFullName}
                            onChange={(e) =>
                              setNewMandirFullName(e.target.value)
                            }
                            placeholder="मंदिर का पूरा नाम"
                          />
                        </div>
                        <div className="col-12">
                          <label className="form-label fw-semibold">पता</label>
                          <input
                            className="form-control"
                            value={newMandirAddress}
                            onChange={(e) =>
                              setNewMandirAddress(e.target.value)
                            }
                            placeholder="मंदिर का पता"
                          />
                        </div>
                        <div className="col-12">
                          <label className="form-label fw-semibold">Remark</label>
                          <textarea
                            className="form-control"
                            value={newMandirRemark}
                            onChange={(e) => setNewMandirRemark(e.target.value)}
                            placeholder="Remark"
                            rows="2"
                          />
                        </div>
                        <div className="col-12 d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-success btn-sm"
                            onClick={handleSubmitNewMandir}
                            disabled={mandirSubmitting}
                          >
                            {mandirSubmitting ? "जोड़ रहे हैं..." : "✅ जोड़ें"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setShowMandirForm(false)}
                          >
                            रद्द करें
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </Field>

                <Field label="जाति प्रकार">
                  <select
                    className="form-select"
                    name="jatiPrakar"
                    value={form.jatiPrakar}
                    onChange={handleChange}
                  >
                    <option value="">जाति प्रकार चुनें</option>
                    {jatiPrakars.map((j) => (
                      <option key={j.id} value={j.name}>
                        {j.name}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="उपजाति प्रकार">
                  <select
                    className="form-select"
                    name="upjatiPrakar"
                    value={form.upjatiPrakar}
                    onChange={handleChange}
                    disabled={!form.jatiPrakar}
                  >
                    <option value="">उपजाति प्रकार चुनें</option>
                    {upjatiPrakars
                      .filter((u) => u.jatiPrakar === form.jatiPrakar)
                      .map((u) => (
                        <option key={u.id} value={u.name}>
                          {u.name}
                        </option>
                      ))}
                  </select>
                </Field>

                <Field label="गोत्र / वंश / अन्य">
                  <select
                    className="form-select"
                    name="gotra"
                    value={form.gotra}
                    onChange={handleChange}
                    disabled={!form.jatiPrakar || !form.upjatiPrakar}
                  >
                    <option value="">गोत्र / वंश / अन्य चुनें</option>
                    {gotras
                      .filter(
                        (g) =>
                          g.jatiPrakar === form.jatiPrakar &&
                          g.upjatiPrakar === form.upjatiPrakar,
                      )
                      .map((g) => (
                        <option key={g.id} value={g.name}>
                          {g.name}
                        </option>
                      ))}
                  </select>
                </Field>

                <Field label="मूर">
                  <select
                    className="form-select"
                    name="moor"
                    value={form.moor}
                    onChange={handleChange}
                    disabled={
                      !form.jatiPrakar || !form.upjatiPrakar || !form.gotra
                    }
                  >
                    <option value="">मूर चुनें</option>
                    {moors
                      .filter(
                        (m) =>
                          m.jatiPrakar === form.jatiPrakar &&
                          m.upjatiPrakar === form.upjatiPrakar &&
                          m.gotra === form.gotra,
                      )
                      .map((m) => (
                        <option key={m.id} value={m.name}>
                          {m.name}
                        </option>
                      ))}
                  </select>
                </Field>

                <Field label="Password *">
                  <input
                    type="password"
                    className="form-control"
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder="पासवर्ड दर्ज करें"
                    required
                  />
                </Field>

                <Field label="Confirm Password *">
                  <input
                    type="password"
                    className="form-control"
                    name="confirmPassword"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    placeholder="पासवर्ड दोबारा दर्ज करें"
                    required
                  />
                </Field>
              </div>

              <div className="text-center mt-4">
                <div className="row g-2 justify-content-center">
                  <div className="col-12 col-sm-6 col-md-4">
                    <button type="submit" className="btn btn-success w-100">
                      {isEditMode ? "💾 अपडेट & देखें" : "💾 Save & Next"}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default RegistrationForm;
